<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DataCard from "@/javascript/views/components/overview/DataCard.vue";
import Tooltip from "@/javascript/views/components/tooltip.vue";
import {useOrganizationStore} from "@/javascript/stores/organizations";
import {CHART_COLORS} from "@/javascript/helpers/chartColors";

// Inlined leaflet.heat plugin (MIT License)
// Source: https://github.com/Leaflet/Leaflet.heat (v0.2.0)
// Includes: simpleheat by Vladimir Agafonkin (https://github.com/mourner/simpleheat)
//
// WHY INLINED: The npm package (leaflet.heat) uses a non-module format that expects
// a global `L` (Leaflet) variable. Vite's ESM bundler cannot resolve this correctly —
// `import "leaflet.heat"` loads the file but doesn't attach L.heatLayer to Leaflet.
// Setting window.L before import also doesn't work reliably with Vite's static analysis.
//
// This is the exact same code from node_modules/leaflet.heat/dist/leaflet-heat.js,
// reformatted for readability. If Vite/ESM compatibility is fixed upstream, this can
// be replaced with: import "leaflet.heat";
(function() {
  function simpleheat(canvas) {
    if (!(this instanceof simpleheat)) return new simpleheat(canvas);
    this._canvas = canvas = typeof canvas === 'string' ? document.getElementById(canvas) : canvas;
    this._ctx = canvas.getContext('2d', { willReadFrequently: true });
    this._width = canvas.width;
    this._height = canvas.height;
    this._max = 1;
    this.clear();
  }

  // takes pixel coordinates and draws blurred colored circles.
  simpleheat.prototype = {
    defaultRadius: 25,
    defaultGradient: { 0.4: 'blue', 0.6: 'cyan', 0.7: 'lime', 0.8: 'yellow', 1: 'red' },
    data: function(data) { this._data = data; return this; },
    max: function(max) { this._max = max; return this; },
    add: function(point) { this._data.push(point); return this; },
    clear: function() { this._data = []; return this; },
    radius: function(r, blur) {
      blur = blur || 15;
      var circle = this._circle = document.createElement('canvas'),
          ctx = circle.getContext('2d'),
          r2 = this._r = r + blur;
      circle.width = circle.height = r2 * 2;
      ctx.shadowOffsetX = ctx.shadowOffsetY = 200;
      ctx.shadowBlur = blur;
      ctx.shadowColor = 'black';
      ctx.beginPath();
      ctx.arc(r2 - 200, r2 - 200, r, 0, Math.PI * 2, true);
      ctx.closePath();
      ctx.fill();
      return this;
    },
    gradient: function(grad) {
      var canvas = document.createElement('canvas'),
          ctx = canvas.getContext('2d'),
          gradient = ctx.createLinearGradient(0, 0, 0, 256);
      canvas.width = 1; canvas.height = 256;
      for (var i in grad) gradient.addColorStop(i, grad[i]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1, 256);
      this._grad = ctx.getImageData(0, 0, 1, 256).data;
      return this;
    },
    draw: function(minOpacity) {
      if (!this._circle) this.radius(this.defaultRadius);
      if (!this._grad) this.gradient(this.defaultGradient);
      var ctx = this._ctx;
      ctx.clearRect(0, 0, this._width, this._height);
      for (var i = 0, len = this._data.length; i < len; i++) {
        var p = this._data[i];
        ctx.globalAlpha = Math.max(p[2] / this._max, minOpacity || 0.05);
        ctx.drawImage(this._circle, p[0] - this._r, p[1] - this._r);
      }
      var colored = ctx.getImageData(0, 0, this._width, this._height);
      this._colorize(colored.data, this._grad);
      ctx.putImageData(colored, 0, 0);
      return this;
    },
    _colorize: function(pixels, gradient) {
      for (var i = 3, len = pixels.length; i < len; i += 4) {
        var j = pixels[i] * 4;
        if (j) { pixels[i - 3] = gradient[j]; pixels[i - 2] = gradient[j + 1]; pixels[i - 1] = gradient[j + 2]; }
      }
    }
  };

  // Converts lat/long points into pixel positions and feeds to simpleheat.
  L.HeatLayer = (L.Layer ? L.Layer : L.Class).extend({
    initialize: function(latlngs, options) { this._latlngs = latlngs; L.setOptions(this, options); },
    setLatLngs: function(latlngs) { this._latlngs = latlngs; return this.redraw(); },
    addLatLng: function(latlng) { this._latlngs.push(latlng); return this.redraw(); },
    setOptions: function(options) { L.setOptions(this, options); if (this._heat) this._updateOptions(); return this.redraw(); },
    redraw: function() { if (this._heat && !this._frame && !this._map._animating) { this._frame = L.Util.requestAnimFrame(this._redraw, this); } return this; },
    onAdd: function(map) {
      this._map = map;
      if (!this._canvas) this._initCanvas();
      map.getPanes().overlayPane.appendChild(this._canvas);
      map.on('moveend', this._reset, this);
      if (map.options.zoomAnimation && L.Browser.any3d) map.on('zoomanim', this._animateZoom, this);
      this._reset();
    },
    onRemove: function(map) {
      map.getPanes().overlayPane.removeChild(this._canvas);
      map.off('moveend', this._reset, this);
      if (map.options.zoomAnimation) map.off('zoomanim', this._animateZoom, this);
    },
    addTo: function(map) { map.addLayer(this); return this; },
    _initCanvas: function() {
      var canvas = this._canvas = L.DomUtil.create('canvas', 'leaflet-heatmap-layer leaflet-layer');
      var originProp = L.DomUtil.testProp(['transformOrigin', 'WebkitTransformOrigin', 'msTransformOrigin']);
      canvas.style[originProp] = '50% 50%';
      var size = this._map.getSize();
      canvas.width = size.x; canvas.height = size.y;
      var animated = this._map.options.zoomAnimation && L.Browser.any3d;
      L.DomUtil.addClass(canvas, 'leaflet-zoom-' + (animated ? 'animated' : 'hide'));
      this._heat = simpleheat(canvas);
      this._updateOptions();
    },
    _updateOptions: function() {
      this._heat.radius(this.options.radius || this._heat.defaultRadius, this.options.blur);
      if (this.options.gradient) this._heat.gradient(this.options.gradient);
      if (this.options.max) this._heat.max(this.options.max);
    },
    _reset: function() {
      var topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(this._canvas, topLeft);
      var size = this._map.getSize();
      if (this._heat._width !== size.x) { this._canvas.width = this._heat._width = size.x; }
      if (this._heat._height !== size.y) { this._canvas.height = this._heat._height = size.y; }
      this._redraw();
    },
    _redraw: function() {
      var r = this._heat._r,
          size = this._map.getSize(),
          bounds = new L.Bounds(L.point([-r, -r]), size.add([r, r])),
          maxZoom = this.options.maxZoom === undefined ? this._map.getMaxZoom() : this.options.maxZoom,
          cellSize = r / 2,
          grid = [],
          panePos = this._map._getMapPanePos(),
          offsetX = panePos.x % cellSize,
          offsetY = panePos.y % cellSize,
          max = this.options.max === undefined ? 1 : this.options.max,
          v = 1 / Math.pow(2, Math.max(0, Math.min(maxZoom - this._map.getZoom(), 12))),
          data = [];

      for (var i = 0, len = this._latlngs.length; i < len; i++) {
        var p = this._map.latLngToContainerPoint(this._latlngs[i]);
        if (bounds.contains(p)) {
          var x = Math.floor((p.x - offsetX) / cellSize) + 2,
              y = Math.floor((p.y - offsetY) / cellSize) + 2,
              alt = this._latlngs[i].alt !== undefined ? this._latlngs[i].alt : this._latlngs[i][2] !== undefined ? +this._latlngs[i][2] : 1,
              k = alt * v;
          grid[y] = grid[y] || [];
          var cell = grid[y][x];
          if (cell) { cell[0] = (cell[0] * cell[2] + p.x * k) / (cell[2] + k); cell[1] = (cell[1] * cell[2] + p.y * k) / (cell[2] + k); cell[2] += k; }
          else { grid[y][x] = [p.x, p.y, k]; }
        }
      }
      for (i = 0, len = grid.length; i < len; i++) {
        if (grid[i]) {
          for (var j = 0, len2 = grid[i].length; j < len2; j++) {
            cell = grid[i][j];
            if (cell) data.push([Math.round(cell[0]), Math.round(cell[1]), Math.min(cell[2], max)]);
          }
        }
      }
      this._heat.data(data).draw(this.options.minOpacity);
      this._frame = null;
    },
    _animateZoom: function(e) {
      var scale = this._map.getZoomScale(e.zoom),
          offset = this._map._getCenterOffset(e.center)._multiplyBy(-scale).subtract(this._map._getMapPanePos());
      if (L.DomUtil.setTransform) L.DomUtil.setTransform(this._canvas, offset, scale);
      else this._canvas.style[L.DomUtil.TRANSFORM] = L.DomUtil.getTranslateString(offset) + ' scale(' + scale + ')';
    }
  });
  L.heatLayer = function(latlngs, options) { return new L.HeatLayer(latlngs, options); };
})();

export default {
  name: "CommunityHeatMap",
  components: { DataCard, Tooltip },
  setup() {
    const organizationStore = useOrganizationStore();
    return { organizationStore };
  },
  data() {
    return {
      map: null,
      heatData: [],
      orgCoords: null,
      loading: true,
      loadingHeat: false,
      hasRetried: false,
      noData: false
    };
  },
  mounted() {
    this.loadDensityData();
  },
  beforeUnmount() {
    // Clean up any existing map to prevent memory leaks
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    async loadDensityData() {
      try {
        const orgId = this.organizationStore.organizationId;

        // Show the map immediately, then load heat data in the background
        this.loading = false;
        this.$nextTick(() => {
          this.loadHeatData(orgId);
        });

      } catch (e) {
        console.error("Error loading density data", e);
        this.noData = true;
        this.loading = false;
      }
    },

    // Fetch Density and add heat layer
    async loadHeatData(orgId) {
      this.loadingHeat = true;
      const response = await fetch(`/organizations/${orgId}/households/density`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.status === 403) {
        this.noData = true;
        this.loadingHeat = false;
        return;
      }

      if (!response.ok) { this.loadingHeat = false; return; }

      const json = await response.json();
      const densityData = json.data;

      // Use server-provided org center coordinates
      if (json.org_center) {
        this.orgCoords = [json.org_center.lat, json.org_center.lng];
      }

      // Initialize the map now that we have the org center
      this.initMap();

      if (!densityData || densityData.length === 0) {
        // No data yet — Sidekiq might be geocoding. Retry once after a delay.
        if (!this.hasRetried) {
          this.hasRetried = true;
          setTimeout(() => this.loadHeatData(orgId), 5000);
        } else {
          this.loadingHeat = false;
        }
        return;
      }

      // Backend returns pre-geocoded coordinates — no Nominatim calls needed
      // Apply random jitter (±300m) for privacy — prevents reverse-engineering exact postal code locations
      const heatPoints = densityData
        .filter(item => item.lat && item.lng)
        .map(item => [
          item.lat + (Math.random() - 0.5) * 0.0054,  // ~300m latitude
          item.lng + (Math.random() - 0.5) * 0.0072,  // ~300m longitude
          item.count
        ]);

      if (heatPoints.length === 0) { this.loadingHeat = false; return; }

      // Add heat layer to existing map
      const maxCount = Math.max(...heatPoints.map(p => p[2]));
      L.heatLayer(heatPoints, {
        radius: 40,
        blur: 20,
        maxZoom: 17,
        max: maxCount,
        minOpacity: 0.4,
        gradient: {
          0.2: CHART_COLORS.heatmap.low,
          0.4: CHART_COLORS.heatmap.medLow,
          0.6: CHART_COLORS.heatmap.medium,
          0.8: CHART_COLORS.heatmap.medHigh,
          1.0: CHART_COLORS.heatmap.high
        }
      }).addTo(this.map);

      // Fit bounds to include heat points
      if (heatPoints.length > 1) {
        const bounds = L.latLngBounds(heatPoints.map(p => [p[0], p[1]]));
        this.map.fitBounds(bounds, { padding: [40, 40] });
      }

      this.loadingHeat = false;
    },

    // Creates base map
    initMap() {
      // Guard against double initialization on retry
      if (this.map) return;

      // Guard against container not being available yet
      if (!this.$refs.mapContainer) return;

      // Use org address as center
      let centerLat, centerLng;

      if (this.orgCoords) {
        centerLat = this.orgCoords[0];
        centerLng = this.orgCoords[1];
      } else {
        centerLat = 42.3;
        centerLng = -83.0;
      }

      // Controls the zoom max and min of map
      this.map = L.map(this.$refs.mapContainer, {
        center: [centerLat, centerLng],
        zoom: 12,
        minZoom: 10,
        maxZoom: 13,
        zoomControl: true,
        scrollWheelZoom: false
      });

      // OpenStreetMap tiles
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18
      }).addTo(this.map);

      // Add legend
      this.addLegend();
    },

    addLegend() {
      const legend = L.control({ position: "bottomright" });
      const colors = CHART_COLORS.heatmap;

      legend.onAdd = () => {
        const div = L.DomUtil.create("div", "heat-legend");
        div.innerHTML = `
          <strong>Density</strong>
          <div class="legend-bar" style="background: linear-gradient(to right, ${colors.low}, ${colors.medLow}, ${colors.medium}, ${colors.medHigh}, ${colors.high})"></div>
          <div class="legend-labels">
            <span>Low</span>
            <span>High</span>
          </div>
        `;
        return div;
      };

      legend.addTo(this.map);
    }
  }
};
</script>

<template>
  <data-card
    title="Community Map"
    title-icon="map-pin"
    icon-color="#10be5a"
    :loaded="!loading"
    bordered
  >
    <template #titleExtras>
      <tooltip
        message="Shows the geographic distribution of your households. Warmer colors indicate higher concentration."
        variant="question"
        size="12px"
        class="ml-1 self-center"
      />
    </template>
  <!--  Only shows if there are no addresses at all -->
    <div v-if="noData" class="flex items-center justify-center h-48 text-gray-400 text-sm">
      No household location data available
    </div>
    <div v-else class="relative" :class="{ 'map-loading-wrapper': loadingHeat }">
      <div ref="mapContainer" class="map-element"></div>
      <div v-if="loadingHeat" class="absolute inset-0 flex items-center justify-center z-[1000] bg-white/40 rounded-lg">
        <span class="bg-white px-4 py-2 rounded-lg shadow-md text-sm text-gray-600 font-medium">
          Loading density data...
        </span>
      </div>
    </div>
  </data-card>
</template>

<style scoped lang="scss">
.map-element {
  @apply w-full rounded-lg;
  height: 400px;
}

.map-loading-wrapper .map-element {
  filter: blur(1px) grayscale(0.3);
  opacity: 0.7;
  transition: filter 0.3s ease, opacity 0.3s ease;
}

:deep(.heat-legend) {
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  font-size: 12px;

  .legend-bar {
    width: 120px;
    height: 12px;
    margin: 6px 0 4px;
    border-radius: 3px;
  }

  .legend-labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #666;
  }
}
</style>
